import { errDiv } from "./mathHelpers";

/**
 * Creates a color scale function that interpolates between a list of colors based on specified bins.
 *
 * @param {Array.<{color: {r: number, g: number, b: number}, maxValue: number}>} colorBins - Array of objects
 * Each element should consist of an rgb color and the max value of the bin.
 * @returns {function(v: number): string} - The color scale function that returns an rgb string.
 * For example, with bins [{green, 0}, {yellow, 0.75}, {red, 1}], value 0.5 will be interpolated using green and yellow by the return function.
 * @throws {Error} - If not enough colors are provided.
 */
export function createColorScale(colorBins) {
  const length = colorBins.length;
  // Check length of lists
  if (length <= 1) {
    throw new Error("At least two colors should be provided.");
  }

  // Return the color scale function
  return function (v) {
    // Return the first or last color when value is out of range
    if (v >= colorBins[length - 1].maxValue) {
      const { r, g, b } = colorBins[length - 1].color;
      return `rgb(${r}, ${g}, ${b})`; // return in rgb style
    }
    if (v <= colorBins[0].maxValue) {
      const { r, g, b } = colorBins[0].color;
      return `rgb(${r}, ${g}, ${b})`; // return in rgb style
    }
    // Find the location of the value
    let idx = 0;
    while (idx < length && v >= colorBins[idx + 1].maxValue) {
      idx++;
    }

    // Interpolate the color within the segment
    const { r, g, b } = interpolateColor(
      colorBins[idx].color,
      colorBins[idx + 1].color,
      errDiv(
        v - colorBins[idx].maxValue,
        colorBins[idx + 1].maxValue - colorBins[idx].maxValue
      )
    );
    return `rgb(${r}, ${g}, ${b})`; // return in rgb style
  };
}

/**
 *
 * @param {Object} startrgb rgb color in the format of { r: r, g: g, b: b }
 * @param {Object} endrgb rgb color in the format of { r: r, g: g, b: b }
 * @param {number} ratio prortion of startrgb color to use, number between 0 and 1
 * @returns rgb color object
 */
export function interpolateColor(startrgb, endrgb, ratio) {
  var r = Math.round(startrgb.r + (endrgb.r - startrgb.r) * ratio);
  var g = Math.round(startrgb.g + (endrgb.g - startrgb.g) * ratio);
  var b = Math.round(startrgb.b + (endrgb.b - startrgb.b) * ratio);

  return { r: r, g: g, b: b };
}

// Define the RGB values for VC ratio
const rgbVCStart = { r: 99, g: 190, b: 123 }; // #63BE7B green
const rgbVCMiddle = { r: 255, g: 235, b: 132 }; // #FFEB84 yellow
const rgbVCEnd = { r: 248, g: 105, b: 107 }; // #F8696B red
// Example usage of createColorScale for V/C rsults
const VCColorScale = createColorScale([
  { color: rgbVCStart, maxValue: 0 },
  { color: rgbVCMiddle, maxValue: 0.85 },
  { color: rgbVCEnd, maxValue: 1 },
]);
// function that reads a number (V/C result) and returns the style object
export const getStylebyVC = (VC) => {
  return { backgroundColor: VCColorScale(VC) };
};
